import { useMemo } from 'react';
import _ from 'lodash';
import { AdjustmentType } from 'enums';
import { useAdjustmentDisputeReasons, useMarDisputeReasons } from 'state';
import { AdjustmentDetail, DisputeReason, DisputeSummary, PaymentDetail } from 'types';

type DisputeRestrictionData = {
    availableDisputeReasons: DisputeReason[],
    isDisputeAllowed: boolean,
    relatedDisputes: DisputeSummary[],
};

export default function useDisputeRestrictions (adjustments: AdjustmentDetail[]): DisputeRestrictionData {
    const adjDisputeReasons = useAdjustmentDisputeReasons();
    const marDisputeReasons = useMarDisputeReasons();
    const adjustmentType = adjustments.length ? adjustments[0].adjustmentTypeId : 0;

    const relatedDisputes = useMemo(
        () => adjustments.reduce((related, { relatedDisputes }) => {
            if (relatedDisputes) {
                relatedDisputes.forEach((dispute) => {
                    if (!related.find((d) => d.disputeNumber === dispute.disputeNumber)) {
                        related.push(dispute);
                    }
                });
            }
            return related;
        }, [] as DisputeSummary[]),
        [adjustments]
    );

    const marBillingCodeIds = useMemo(() => {
        const billingCodeIds = adjustments
            .map(adjustment => (adjustment as PaymentDetail).marBillingCodeId ?? 0)
            .filter(marBillingCodeId => marBillingCodeId > 0);

        return Array.from(new Set(billingCodeIds));
    }, [adjustments]);

    const availableMarDisputeReasons = useMemo(
        () => _.uniqBy(
            marDisputeReasons.filter(reason =>
                marBillingCodeIds.includes(reason.marBillingCodeId) &&
                !relatedDisputes.find(({ disputeReasonId }) => disputeReasonId === reason.disputeReasonId)
            ),
            reason => reason.disputeReasonId
        ).sort((a, b) => a.description.localeCompare(b.description)),
        [marBillingCodeIds, marDisputeReasons, relatedDisputes]
    );


    const availableAdjDisputeReasons = useMemo(
        () => adjDisputeReasons.filter(reason => reason.adjustmentTypeId === adjustmentType &&
            !relatedDisputes.find(({ disputeReasonId }) => disputeReasonId === reason.disputeReasonId)
        ).sort((a, b) => a.description.localeCompare(b.description)),
        [adjustmentType, adjDisputeReasons, relatedDisputes]
    );

    const availableDisputeReasons = marBillingCodeIds.length
        ? availableMarDisputeReasons
        : availableAdjDisputeReasons;

    const isDisputeAllowed = (adjustmentType === AdjustmentType.CustomerDeduction
                                || adjustmentType === AdjustmentType.CustomerDedRepay
                                || adjustmentType === AdjustmentType.DotCreditToSupplier
                                || adjustmentType === AdjustmentType.DotBillback)
        ? !_.isEmpty(availableDisputeReasons)
        : _.isEmpty(relatedDisputes);

    return {
        availableDisputeReasons,
        isDisputeAllowed,
        relatedDisputes
    };
}
